/**
 * Storage Utility
 * Centralized storage management for localStorage, sessionStorage, and cookies
 */

import { STORAGE_KEYS } from '../constants';

// Storage types
export const STORAGE_TYPES = {
  LOCAL: 'localStorage',
  SESSION: 'sessionStorage',
  COOKIE: 'cookie',
};

// Default storage type
const DEFAULT_STORAGE_TYPE = STORAGE_TYPES.LOCAL;

/**
 * Get storage instance based on type
 */

const getStorage = (type = DEFAULT_STORAGE_TYPE) => {
  switch (type) {
    case STORAGE_TYPES.LOCAL:
      return window.localStorage;
    case STORAGE_TYPES.SESSION:
      return window.sessionStorage;
    case STORAGE_TYPES.COOKIE:
      return document.cookie;
    default:
      return window.localStorage;
  }
};

/**
 * Check if storage is available
 */
const isStorageAvailable = (type = DEFAULT_STORAGE_TYPE) => {
  try {
    const storage = getStorage(type);
    if (type === STORAGE_TYPES.COOKIE) {
      return navigator.cookieEnabled;
    }
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Set item in storage
 */
export const setItem = (key, value, type = DEFAULT_STORAGE_TYPE, options = {}) => {
  try {
    if (!isStorageAvailable(type)) {
      throw new Error(`Storage type ${type} is not available`);
    }

    const storage = getStorage(type);
    const serializedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

    if (type === STORAGE_TYPES.COOKIE) {
      // Cookie specific options
      const { expires, path = '/', domain, secure, sameSite = 'Lax' } = options;
      let cookieString = `${key}=${encodeURIComponent(serializedValue)}`;
      
      if (expires) {
        const expiryDate = new Date(expires);
        cookieString += `; expires=${expiryDate.toUTCString()}`;
      }
      
      if (path) cookieString += `; path=${path}`;
      if (domain) cookieString += `; domain=${domain}`;
      if (secure) cookieString += '; secure';
      if (sameSite) cookieString += `; samesite=${sameSite}`;
      
      document.cookie = cookieString;
    } else {
      storage.setItem(key, serializedValue);
    }

    return true;
  } catch (error) {
    console.error('Error setting storage item:', error);
    return false;
  }
};

/**
 * Get item from storage
 */
export const getItem = (key, type = DEFAULT_STORAGE_TYPE, defaultValue = null) => {
  try {
    if (!isStorageAvailable(type)) {
      return defaultValue;
    }

    const storage = getStorage(type);
    let value;

    if (type === STORAGE_TYPES.COOKIE) {
      const cookies = document.cookie.split(';');
      const cookie = cookies.find(c => c.trim().startsWith(`${key}=`));
      value = cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
    } else {
      value = storage.getItem(key);
    }

    if (value === null) {
      return defaultValue;
    }

    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  } catch (error) {
    console.error('Error getting storage item:', error);
    return defaultValue;
  }
};

/**
 * Remove item from storage
 */
export const removeItem = (key, type = DEFAULT_STORAGE_TYPE) => {
  try {
    if (!isStorageAvailable(type)) {
      return false;
    }

    const storage = getStorage(type);

    if (type === STORAGE_TYPES.COOKIE) {
      // Set cookie with past expiry date to remove it
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } else {
      storage.removeItem(key);
    }

    return true;
  } catch (error) {
    console.error('Error removing storage item:', error);
    return false;
  }
};

/**
 * Clear all items from storage
 */
export const clear = (type = DEFAULT_STORAGE_TYPE) => {
  try {
    if (!isStorageAvailable(type)) {
      return false;
    }

    const storage = getStorage(type);

    if (type === STORAGE_TYPES.COOKIE) {
      // Remove all cookies by setting them with past expiry
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const key = cookie.split('=')[0].trim();
        document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
    } else {
      storage.clear();
    }

    return true;
  } catch (error) {
    console.error('Error clearing storage:', error);
    return false;
  }
};

/**
 * Get all keys from storage
 */
export const getKeys = (type = DEFAULT_STORAGE_TYPE) => {
  try {
    if (!isStorageAvailable(type)) {
      return [];
    }

    const storage = getStorage(type);

    if (type === STORAGE_TYPES.COOKIE) {
      return document.cookie.split(';').map(cookie => cookie.split('=')[0].trim());
    } else {
      return Object.keys(storage);
    }
  } catch (error) {
    console.error('Error getting storage keys:', error);
    return [];
  }
};

/**
 * Check if key exists in storage
 */
export const hasKey = (key, type = DEFAULT_STORAGE_TYPE) => {
  try {
    if (!isStorageAvailable(type)) {
      return false;
    }

    const storage = getStorage(type);

    if (type === STORAGE_TYPES.COOKIE) {
      return document.cookie.includes(`${key}=`);
    } else {
      return storage.getItem(key) !== null;
    }
  } catch (error) {
    console.error('Error checking storage key:', error);
    return false;
  }
};

/**
 * Get storage size (approximate for cookies)
 */
export const getSize = (type = DEFAULT_STORAGE_TYPE) => {
  try {
    if (!isStorageAvailable(type)) {
      return 0;
    }

    const storage = getStorage(type);

    if (type === STORAGE_TYPES.COOKIE) {
      return document.cookie.length;
    } else {
      let size = 0;
      for (let key in storage) {
        if (storage.hasOwnProperty(key)) {
          size += storage[key].length + key.length;
        }
      }
      return size;
    }
  } catch (error) {
    console.error('Error getting storage size:', error);
    return 0;
  }
};

// Convenience methods for common storage operations
export const storageUtils = {
  getItem,
  setItem,
  removeItem,
  // Auth token management
  setAuthToken: (token) => setItem(STORAGE_KEYS.AUTH_TOKEN, token),
  getAuthToken: () => getItem(STORAGE_KEYS.AUTH_TOKEN),
  removeAuthToken: () => removeItem(STORAGE_KEYS.AUTH_TOKEN),
  
  // User data management
  setUserData: (userData) => setItem(STORAGE_KEYS.USER_DATA, userData),
  getUserData: () => getItem(STORAGE_KEYS.USER_DATA),
  removeUserData: () => removeItem(STORAGE_KEYS.USER_DATA),
  
  // Theme management
  setTheme: (theme) => setItem(STORAGE_KEYS.THEME, theme),
  getTheme: () => getItem(STORAGE_KEYS.THEME, DEFAULT_STORAGE_TYPE, 'light'),
  removeTheme: () => removeItem(STORAGE_KEYS.THEME),
  
  // Language management
  setLanguage: (language) => setItem(STORAGE_KEYS.LANGUAGE, language),
  getLanguage: () => getItem(STORAGE_KEYS.LANGUAGE, DEFAULT_STORAGE_TYPE, 'en'),
  removeLanguage: () => removeItem(STORAGE_KEYS.LANGUAGE),
  
  // Preferences management
  setPreferences: (preferences) => setItem(STORAGE_KEYS.PREFERENCES, preferences),
  getPreferences: () => getItem(STORAGE_KEYS.PREFERENCES, DEFAULT_STORAGE_TYPE, {}),
  removePreferences: () => removeItem(STORAGE_KEYS.PREFERENCES),
  
  // Clear all auth-related data
  clearAuth: () => {
    removeItem(STORAGE_KEYS.AUTH_TOKEN);
    removeItem(STORAGE_KEYS.USER_DATA);
  },
  
  // Clear all app data
  clearAll: () => {
    clear();
  },
};

export default storageUtils; 